/**
 * App preview contracts: a school admin previews the school's own mobile app,
 * live in the browser, on a simulator or emulator the mobile lab runs
 * (tooling/mobile-lab, `previews.ts`).
 *
 *   POST /v1/schools/:schoolId/app-preview        start one
 *   GET  /v1/schools/:schoolId/app-preview/:id    where it is
 *   POST /v1/schools/:schoolId/app-preview/:id/end   stop it early
 *
 * The preview opens signed in as the requester, or, for a caller who may
 * impersonate users at the school, as `viewAs`. The `viewerUrl` is a
 * capability URL: whoever holds it can watch and tap that one device until
 * the preview ends, so the web app opens it for the requester only.
 */

import { z } from 'zod'

export const AppPreviewPlatformSchema = z.enum(['ios', 'android'])
export type AppPreviewPlatform = z.infer<typeof AppPreviewPlatformSchema>

export const AppPreviewStatusSchema = z.enum([
  'queued',
  'building',
  'booting',
  'installing',
  'opening',
  'ready',
  'ended',
  'failed'
])
export type AppPreviewStatus = z.infer<typeof AppPreviewStatusSchema>

/** Where the device runs: the mobile lab, or an EAS Simulator session (iPhone only). */
export const AppPreviewEngineSchema = z.enum(['lab', 'eas'])
export type AppPreviewEngine = z.infer<typeof AppPreviewEngineSchema>

export const CreateAppPreviewRequestSchema = z.object({
  platform: AppPreviewPlatformSchema.default('ios'),
  engine: AppPreviewEngineSchema.default('lab'),
  /** A school user public id to preview as; needs `impersonate_users` at the school. */
  viewAs: z.string().min(1).optional()
})
export type CreateAppPreviewRequest = z.infer<typeof CreateAppPreviewRequestSchema>

export const AppPreviewSchema = z.object({
  id: z.string(),
  platform: AppPreviewPlatformSchema,
  status: AppPreviewStatusSchema,
  /** What the lab is doing now, in words, for the progress line. */
  step: z.string().nullable(),
  error: z.string().nullable(),
  viewerUrl: z.string().url(),
  /** The commit whose release build the preview runs. */
  commit: z.string().nullable(),
  createdAt: z.string(),
  readyAt: z.string().nullable(),
  endsAt: z.string()
})
export type AppPreview = z.infer<typeof AppPreviewSchema>

export const AppPreviewResponseSchema = z.object({ preview: AppPreviewSchema })
export type AppPreviewResponse = z.infer<typeof AppPreviewResponseSchema>
